import { ApolloClient, InMemoryCache } from '@apollo/client/core';
import {
  createAuth0Client,
  Auth0Client,
  Auth0ClientOptions,
  User as Auth0User,
  GetTokenSilentlyOptions,
  LogoutOptions,
  RedirectLoginOptions,
  RedirectLoginResult,
} from '@auth0/auth0-spa-js';
import { reactive, watch, type App } from 'vue';
import { apiObject } from 'rudder-sdk-js';

import { Role } from '@freelancerepublik/graphql-types';

import {
  FREELANCE_STATUS_OPTIONS,
  FreelanceTrait,
  EXPECTED_CONTRACT_OPTIONS,
} from '@freelancerepublik/models-core/Freelance.core';
import { hasRoles } from '@freelancerepublik/models-core/Role.core';
import { UserTrait } from '@freelancerepublik/models-core/User.core';
import { getLabelByValue } from '@freelancerepublik/models-core/utils.core';
import { LoggedUserDocument, LoggedUserQuery } from './auth.generated';

import { ENV } from '@/env';

type LoggedUser = LoggedUserQuery['loggedUser'] | null;

function isJwtValid(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' && decoded.exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

function clearRopgTokens() {
  localStorage.removeItem('ropg_access_token');
  localStorage.removeItem('ropg_refresh_token');
}

export interface VueAuth0InstanceType {
  loginWithRedirect: (o: RedirectLoginOptions<any>) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  signupWithCredentials: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  getTokenSilently: (o?: GetTokenSilentlyOptions) => Promise<string | undefined>;
  getAccessToken: () => Promise<string | undefined>;
  logout: (o?: LogoutOptions) => Promise<void>;
  refreshLoggedUser: () => Promise<void>;
  setLoggedUser: (user: LoggedUser) => void;
  isAuthenticated: boolean;
  loading: boolean;
  error: unknown | null;
  auth0User: Auth0User | undefined | null;
  loggedUser: LoggedUser;
  isFreelance: boolean;
  isClient: boolean;
  /** @internal Expose the reactive state for external watch() usage */
  _state: AuthState;
}

import { authKey } from '@/infra/injection-keys';
export { authKey };

type AuthState = {
  loading: boolean;
  isAuthenticated: boolean;
  auth0Client: Auth0Client | null;
  auth0User: Auth0User | undefined | null;
  loggedUser: LoggedUser;
  error: unknown | null;
  ropgAccessToken: string | null;
};

function createAuthInstance(options: Options): VueAuth0InstanceType {
  const storedRopgToken = localStorage.getItem('ropg_access_token');
  const validRopgToken = storedRopgToken && isJwtValid(storedRopgToken) ? storedRopgToken : null;
  if (storedRopgToken && !validRopgToken) {
    clearRopgTokens();
  }

  const state = reactive<AuthState>({
    loading: true,
    isAuthenticated: false,
    auth0Client: null,
    auth0User: null,
    loggedUser: null,
    error: null,
    ropgAccessToken: validRopgToken,
  });

  // Rudderstack & Sentry references (set after plugin registration via globals)
  let rudderanalytics: any = null;
  let sentry: any = null;

  function setLoggedUser(user: LoggedUser) {
    state.loggedUser = user;

    if (state.loggedUser && sentry) {
      sentry.getCurrentScope().setUser({
        email: state.loggedUser?.email,
        id: state.loggedUser?._id,
      });
    }

    if (state.loggedUser && rudderanalytics) {
      const userProperties = {
        createdAt: state.loggedUser.createdAt,
        email: state.loggedUser.email,
        fullname: state.loggedUser.fullname,
        firstname: state.loggedUser.firstname,
        lastname: state.loggedUser.lastname,
        roles: state.loggedUser.roles,
      } satisfies { [key in UserTrait]: any };

      const isFreelance = hasRoles(state.loggedUser as any, [Role.Freelance]);

      let properties = userProperties as Record<string, any>;
      if (isFreelance) {
        properties = {
          ...userProperties,
          expectedContract: getLabelByValue(EXPECTED_CONTRACT_OPTIONS, state.loggedUser.freelance!.expectedContract),
          source: state.loggedUser.freelance!.source,
          jobCategories: state.loggedUser.freelance!.jobCategories,
          status: getLabelByValue(FREELANCE_STATUS_OPTIONS, state.loggedUser.freelance!.status!),
        } satisfies { [key in FreelanceTrait]: any };
      }

      rudderanalytics.identify(state.loggedUser._id as string, properties as apiObject);
      rudderanalytics.trackUserLogin();
    }
  }

  const instance: VueAuth0InstanceType = {
    get loading() { return state.loading; },
    get isAuthenticated() { return state.isAuthenticated; },
    get auth0User() { return state.auth0User; },
    get loggedUser() { return state.loggedUser; },
    get error() { return state.error; },
    get isFreelance() {
      return hasRoles(state.loggedUser as any, [Role.Freelance]);
    },
    get isClient() {
      return hasRoles(state.loggedUser as any, [Role.Client]);
    },

    _state: state,

    async loginWithRedirect(o: RedirectLoginOptions<any> & { connection?: string }) {
      const { connection, ...rest } = o;
      return state.auth0Client?.loginWithRedirect({
        authorizationParams: {
          redirect_uri: options.defaultRedirectUri,
          ...(connection && { connection }),
          ...rest.authorizationParams,
        },
        ...rest,
      });
    },

    getTokenSilently(o?: GetTokenSilentlyOptions) {
      return state.auth0Client?.getTokenSilently(o);
    },

    async getAccessToken(): Promise<string | undefined> {
      if (state.ropgAccessToken) {
        if (isJwtValid(state.ropgAccessToken)) {
          return state.ropgAccessToken;
        }
        state.ropgAccessToken = null;
        clearRopgTokens();
      }
      return state.auth0Client?.getTokenSilently();
    },

    async loginWithCredentials(email: string, password: string): Promise<void> {
      const { domain, clientId } = options.auth0ClientOptions;
      const audience = options.auth0ClientOptions.authorizationParams?.audience as string;

      const response = await fetch(`https://${domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'password',
          client_id: clientId,
          username: email,
          password,
          audience,
          scope: 'openid profile email offline_access',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || 'Login failed');
      }

      const tokens = await response.json();

      state.ropgAccessToken = tokens.access_token;
      localStorage.setItem('ropg_access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('ropg_refresh_token', tokens.refresh_token);
      }

      state.isAuthenticated = true;
      state.auth0User = { email };

      await new Promise<void>((resolve, reject) => {
        options.apolloClient
          .watchQuery({
            query: LoggedUserDocument,
            fetchPolicy: 'network-only',
          })
          .subscribe({
            next({ data }: { data: { loggedUser: LoggedUser } }) {
              setLoggedUser(data.loggedUser);
              state.loading = false;
              resolve();
            },
            error(err: Error) {
              state.loading = false;
              state.ropgAccessToken = null;
              clearRopgTokens();
              state.isAuthenticated = false;
              reject(err);
            },
          });
      });
    },

    async signupWithCredentials(email: string, password: string): Promise<void> {
      const { domain, clientId } = options.auth0ClientOptions;

      const response = await fetch(`https://${domain}/dbconnections/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email,
          password,
          connection: 'Username-Password-Authentication',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Signup failed');
      }

      await instance.loginWithCredentials(email, password);
    },

    async forgotPassword(email: string): Promise<void> {
      const { domain, clientId } = options.auth0ClientOptions;

      const response = await fetch(`https://${domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email,
          connection: 'Username-Password-Authentication',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || 'Password reset failed');
      }
    },

    async logout(o: LogoutOptions = {}) {
      await options.apolloClient.clearStore();

      state.ropgAccessToken = null;
      clearRopgTokens();
      state.isAuthenticated = false;
      state.loggedUser = null;
      state.auth0User = null;
      state.error = null;

      return state.auth0Client?.logout({
        logoutParams: {
          returnTo: options.logoutReturnTo,
        },
        ...o,
      });
    },

    setLoggedUser,

    async refreshLoggedUser() {
      await options.apolloClient.refetchQueries({
        include: ['loggedUser'],
      });
    },
  };

  // Initialize auth0 client (async IIFE)
  (async () => {
    state.auth0Client = await createAuth0Client({
      ...options.auth0ClientOptions,
      authorizationParams: {
        redirect_uri: window.location.origin,
        ...options.auth0ClientOptions.authorizationParams,
      },
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      cacheLocation: 'localstorage',
    });

    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      const { appState } = await state.auth0Client.handleRedirectCallback();
      state.error = null;

      if (options.onRedirectCallback) {
        options.onRedirectCallback(appState);
      }
    }

    state.isAuthenticated = await state.auth0Client.isAuthenticated();
    state.auth0User = await state.auth0Client.getUser();

    if (!state.isAuthenticated && state.ropgAccessToken && isJwtValid(state.ropgAccessToken)) {
      state.isAuthenticated = true;
    }

    if (state.isAuthenticated) {
      try {
        const data = options.apolloClient.readQuery({ query: LoggedUserDocument });

        if (data.loggedUser) {
          setLoggedUser(data.loggedUser);
          state.loading = false;
        }
      } catch (e) {
        if (ENV.NODE_ENV === 'development') {
          console.info('[DEBUG] No logged user in cache !');
        }
      }

      options.apolloClient
        .watchQuery({
          query: LoggedUserDocument,
          fetchPolicy: 'network-only',
        })
        .subscribe({
          next({ data }: { data: { loggedUser: LoggedUser } }) {
            setLoggedUser(data.loggedUser);
            state.loading = false;
          },
          error(err: Error) {
            state.error = err;
            state.loading = false;
            state.ropgAccessToken = null;
            clearRopgTokens();
            state.isAuthenticated = false;
          },
        });
    } else {
      state.loading = false;
    }
  })();

  // Allow setting rudderanalytics & sentry references after creation
  (instance as any)._setRudderanalytics = (ra: any) => { rudderanalytics = ra; };
  (instance as any)._setSentry = (s: any) => { sentry = s; };

  return instance;
}

let instance: VueAuth0InstanceType;

/** Returns the current instance of the auth wrapper */
export const getInstance = () => instance;

type Options = {
  auth0ClientOptions: Auth0ClientOptions;
  apolloClient: ApolloClient<InMemoryCache>;
  defaultRedirectUri: string;
  logoutReturnTo: string;
  onRedirectCallback?: (appState: RedirectLoginResult<any>) => void;
};

export const AuthPlugin = {
  install(app: App, options: Options) {
    instance = createAuthInstance(options);

    app.provide(authKey, instance);
  },
};
